import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/utils-image";
import { toast } from "sonner";
import { Loader2, Plus, Save } from "lucide-react";

const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

export function MonthlyBudgetEditor({ currency }) {
  const [period, setPeriod] = useState(() => today().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [opening, setOpening] = useState(5000);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pendingSupport = useRef(null);
  const [year, month] = period.split("-").map(Number);
  const endpoint = `/settings/monthly/${year}/${month}`;
  const endDate = `${period}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setSummary(null);
    setAmount(""); setNotes(""); pendingSupport.current = null;
    setDate(today().startsWith(period) ? today() : `${period}-01`);
    api.get(endpoint).then(({ data }) => {
      if (active) { setSummary(data); setOpening(data.opening_base); }
    }).catch((err) => {
      if (active) setError(err?.response?.data?.detail || "تعذر تحميل ميزانية الشهر");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [endpoint, period]);

  const saveOpening = async (event) => {
    event.preventDefault();
    if (busy || loading || !summary) return;
    if (opening === "" || !Number.isFinite(Number(opening)) || Number(opening) < 0) {
      toast.error("أدخل رصيداً افتتاحياً صحيحاً لا يقل عن صفر"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.put(endpoint, { opening_balance: Number(opening) });
      setSummary(data); setOpening(data.opening_base);
      toast.success("تم حفظ الرصيد الافتتاحي للشهر المحدد");
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذر الحفظ"); }
    finally { setBusy(false); }
  };

  const addSupport = async (event) => {
    event.preventDefault();
    if (busy || loading || !summary) return;
    if (!Number.isFinite(Number(amount)) || Number(amount) < 0.01 || !date.startsWith(`${period}-`)) {
      toast.error("أدخل مبلغ الدعم وتاريخ التحويل ضمن الشهر المحدد"); return;
    }
    const content = { amount: Number(amount), transfer_date: date, notes };
    const fingerprint = JSON.stringify(content);
    if (pendingSupport.current?.fingerprint !== fingerprint) {
      pendingSupport.current = { fingerprint, payload: { ...content, id: crypto.randomUUID() } };
    }
    setBusy(true);
    try {
      const { data } = await api.post(`${endpoint}/supports`, pendingSupport.current.payload);
      setSummary(data); setAmount(""); setNotes(""); pendingSupport.current = null;
      toast.success("تمت إضافة الدعم إلى الشهر المحدد");
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذر إضافة الدعم؛ يمكنك إعادة المحاولة"); }
    finally { setBusy(false); }
  };

  const buttonClass = "bg-[#1F3A33] text-white rounded-md px-4 py-3 font-readex inline-flex items-center gap-2 disabled:opacity-50";
  const disabled = busy || loading || !summary;
  return (
    <section className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6 space-y-5" data-testid="monthly-budget-editor">
      <div>
        <h2 className="font-readex font-medium text-xl text-[#1A1C19]">مصروف الشهر والدعم الإضافي</h2>
        <p className="text-sm text-[#5C605A] mt-2">لكل شهر رصيد مستقل. يبدأ الشهر الجديد بـ5,000 افتراضياً، ويمكن تعديله دون تغيير تمويل الأشهر السابقة.</p>
      </div>
      <label className="block">
        <span className="block text-sm mb-2">الشهر</span>
        <input aria-label="الشهر" type="month" min="1900-01" max="2200-12" className="input" value={period} disabled={busy}
          onChange={(e) => { if (/^\d{4}-\d{2}$/.test(e.target.value)) setPeriod(e.target.value); }} />
      </label>
      {loading && <p role="status" className="flex gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />جاري تحميل ميزانية الشهر…</p>}
      {error && <p role="alert" className="text-[#C84C31]">{error}</p>}
      <form onSubmit={saveOpening} className="space-y-3">
        <label className="block">
          <span className="block text-sm mb-2">الرصيد الافتتاحي للشهر المحدد</span>
          <input type="number" min="0" max="1000000000" step="0.01" required className="input font-mono-num" data-testid="opening-input"
            disabled={disabled} value={opening} onChange={(e) => setOpening(e.target.value)} />
        </label>
        <button type="submit" disabled={disabled} className={buttonClass}><Save className="w-4 h-4" />حفظ رصيد الشهر</button>
      </form>
      {summary && <dl className="grid grid-cols-2 gap-3 rounded-md bg-[#F5F4EF] p-4 text-sm">
        <dt>الرصيد الافتتاحي المحفوظ</dt><dd className="font-mono-num text-end">{formatCurrency(summary.opening_base, currency)}</dd>
        <dt>إجمالي الدعم</dt><dd className="font-mono-num text-end">{formatCurrency(summary.support_total, currency)}</dd>
        <dt className="font-medium">إجمالي تمويل الشهر</dt><dd className="font-mono-num text-end font-medium">{formatCurrency(summary.funding_total, currency)}</dd>
        <dt>المرحّل</dt><dd className="font-mono-num text-end">{formatCurrency(summary.carry_over, currency)}</dd>
        <dt>الفواتير</dt><dd className="font-mono-num text-end">{formatCurrency(summary.total_expense, currency)}</dd>
        <dt className="font-medium">المتبقي</dt><dd className="font-mono-num text-end font-medium">{formatCurrency(summary.closing_balance, currency)}</dd>
      </dl>}
      <form onSubmit={addSupport} className="space-y-3 border-t border-[#1A1C19]/10 pt-5">
        <h3 className="font-readex font-medium">إضافة دعم من الشركة</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label><span className="block text-sm mb-2">مبلغ الدعم</span><input aria-label="مبلغ الدعم" className="input" type="number" min="0.01" max="1000000000" step="0.01" required disabled={disabled} value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          <label><span className="block text-sm mb-2">تاريخ التحويل</span><input aria-label="تاريخ التحويل" className="input" type="date" min={`${period}-01`} max={endDate} required disabled={disabled} value={date} onChange={(e) => setDate(e.target.value)} /></label>
        </div>
        <label className="block"><span className="block text-sm mb-2">ملاحظات (اختياري)</span><input className="input" maxLength={500} disabled={disabled} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <button type="submit" disabled={disabled} className={buttonClass}><Plus className="w-4 h-4" />إضافة الدعم</button>
      </form>
      <div className="space-y-2">
        <h3 className="font-readex text-sm font-medium">سجل دعم الشهر</h3>
        {!loading && summary && !summary.supports?.length && <p className="text-sm text-[#8A8D86]">لا يوجد دعم إضافي لهذا الشهر.</p>}
        {summary?.supports?.map((support) => <div key={support.id} className="border rounded-md p-3 text-sm flex flex-wrap justify-between gap-2">
          <div><div dir="ltr">{support.transfer_date}</div>{support.notes && <p className="mt-1 text-[#5C605A] break-words">{support.notes}</p>}</div>
          <span className="font-mono-num text-[#1F3A33]">+{formatCurrency(support.amount, currency)}</span>
        </div>)}
      </div>
    </section>
  );
}
